import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { CARD_BORDER } from '../../components/Section';
import COLORS from '../../constants/colors';
import { getSaveChartColor } from '../../utils/charts';
import { round2 } from '../../utils/number';

interface AssetAllocationListProps {
  /** The same array the doughnut renders, so the list and the chart cannot disagree. */
  items: { label: string; value: number }[];
}

/**
 * Read-only allocation readout.
 *
 * This replaced a stack of `disabled` Selects and `disabled` Sliders: the data
 * was never editable here (weights are edited under Associated Funds/Index), so
 * every row was paying full form-control height, and repeating the 0-100% slider
 * scale once per row. Swatch colours come from `getSaveChartColor` with the same
 * arguments the chart uses, so a row's colour always matches its slice.
 */
export function AssetAllocationList({ items }: AssetAllocationListProps) {
  // Colour is derived before filtering so indexes still line up with the chart's.
  const rows = items
    .map((item, index) => ({
      label: item.label,
      value: Number(item.value) || 0,
      color: getSaveChartColor(items.length, index),
    }))
    .filter((row) => row.label);

  const total = round2(rows.reduce((sum, row) => sum + row.value, 0));
  const isBalanced = total === 100;
  const totalColor = isBalanced ? COLORS.SECONDARY_GREEN : COLORS.ORANGE;

  if (!rows.length) {
    return (
      <Typography sx={{ fontSize: '0.875rem', color: COLORS.LIGHT_GRAY, py: '12px' }}>
        No allocation to show yet.
      </Typography>
    );
  }

  return (
    <Box>
      {rows.map((row, index) => (
        <Box
          key={`${row.label}-${row.color}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            py: '10px',
            borderTop: index === 0 ? 'none' : `1px solid ${CARD_BORDER}`,
          }}
        >
          <Box sx={{ width: '10px', height: '10px', borderRadius: '3px', backgroundColor: row.color, flexShrink: 0 }} />
          <Typography
            title={row.label}
            sx={{
              flex: '0 0 38%',
              minWidth: 0,
              fontSize: '0.875rem',
              color: COLORS.MAIN_DARK,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.label}
          </Typography>
          {/* Scaled against 100%, not against the largest row, so a 2% sliver reads as 2%. */}
          <Box
            sx={{
              flex: '1 1 auto',
              minWidth: '60px',
              height: '6px',
              borderRadius: '3px',
              backgroundColor: alpha(COLORS.MAIN_DARK, 0.06),
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: `${Math.min(Math.max(row.value, 0), 100)}%`,
                height: '100%',
                borderRadius: '3px',
                backgroundColor: row.color,
              }}
            />
          </Box>
          <Typography
            sx={{
              flex: '0 0 52px',
              textAlign: 'right',
              fontSize: '0.875rem',
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              color: COLORS.MAIN_DARK,
            }}
          >
            {round2(row.value)}%
          </Typography>
        </Box>
      ))}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          mt: '4px',
          pt: '12px',
          borderTop: `1px solid ${CARD_BORDER}`,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.6875rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: COLORS.LIGHT_GRAY,
          }}
        >
          Total allocation
        </Typography>
        <Box
          sx={{
            px: '10px',
            py: '3px',
            borderRadius: '999px',
            fontSize: '0.8125rem',
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            color: totalColor,
            backgroundColor: alpha(totalColor, 0.12),
          }}
        >
          {total}%
        </Box>
      </Box>
    </Box>
  );
}
